import { utils, writeFileXLSX, read } from "xlsx";
import { setSnackbarDetails } from "../redux/AuthReducer/AuthSlice";
import { apiClient } from "./apiClient";
import { OptionType } from "../components/custom-select/Select";

type columns = {
  header: string;
  accessor: string;
};

const getSafeExcelSheetName = (name: string) => {
  const safeName = name
    .replace(/\.xlsx$/i, "")
    .replace(/[:\\/?*[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, 31)
    .trim();

  return safeName || "Sheet1";
};

export const parseCustomDate = (dateString: string) => {
  if (!dateString) return null;

  const [datePart, timePart, modifier] = dateString.split(" ");

  let [year, month, day] = datePart.split("-").map(Number);
  let [hours, minutes] = timePart.split(":").map(Number);

  if (modifier === "PM" && hours !== 12) {
    hours += 12;
  }

  if (modifier === "AM" && hours === 12) {
    hours = 0;
  }

  return new Date(year, month - 1, day, hours, minutes);
};

export const formatDateOnly = (date: Date | string | null) => {
  if (!date) return "";

  const parsedDate = typeof date === "string" ? new Date(date) : date;
  if (isNaN(parsedDate.getTime())) return "";

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`; // ✅ LOCAL DATE (no timezone shift)
};

export const formatDateTimeDisplay = (
  date: Date | string | null | undefined,
) => {
  if (!date) return "-";

  const parsedParts =
    typeof date === "string" ? parseDateTimeParts(date) : getDateTimeParts(date);

  if (!parsedParts) return "-";

  const hour12 = parsedParts.hour % 12 || 12;
  const meridian = parsedParts.hour >= 12 ? "PM" : "AM";

  return `${parsedParts.year} - ${parsedParts.month} - ${parsedParts.day} | ${padDateTimePart(
    hour12,
  )}:${parsedParts.minute} ${meridian}`;
};

export const formatTime = (
  date: Date | string | null,
  formatHourStatus: boolean,
) => {
  if (!date) return "";
  const parsedDate =
    typeof date === "string" ? new Date(`1970-01-01 ${date}`) : date;
  if (isNaN(parsedDate.getTime())) return "";
  return parsedDate.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: formatHourStatus,
  });
};

type DateTimeParts = {
  year: string;
  month: string;
  day: string;
  hour: number;
  minute: string;
};

const getDateTimeParts = (date: Date): DateTimeParts | null => {
  if (isNaN(date.getTime())) return null;

  return {
    year: String(date.getFullYear()),
    month: padDateTimePart(date.getMonth() + 1),
    day: padDateTimePart(date.getDate()),
    hour: date.getHours(),
    minute: padDateTimePart(date.getMinutes()),
  };
};

const parseDateTimeParts = (dateString: string): DateTimeParts | null => {
  const normalizedDate = dateString.trim();
  const match = normalizedDate.match(
    /^(\d{4})\s*-\s*(\d{1,2})\s*-\s*(\d{1,2})(?:[T\s|,-]+(\d{1,2})(?::(\d{2}))?(?:\s*:\s*\d{2})?(?:\.\d+)?\s*(AM|PM)?)?/i,
  );

  if (!match) {
    return getDateTimeParts(new Date(normalizedDate));
  }

  const year = match[1];
  const month = padDateTimePart(match[2]);
  const day = padDateTimePart(match[3]);
  const rawHour = match[4] ? Number(match[4]) : 0;
  const minute = padDateTimePart(match[5] ?? 0);
  const meridian = match[6]?.toUpperCase();
  let hour = rawHour;

  if (meridian === "PM" && hour < 12) hour += 12;
  if (meridian === "AM" && hour === 12) hour = 0;

  if (
    Number(month) < 1 ||
    Number(month) > 12 ||
    Number(day) < 1 ||
    Number(day) > 31 ||
    hour < 0 ||
    hour > 23 ||
    Number(minute) > 59
  ) {
    return null;
  }

  return { year, month, day, hour, minute };
};

const padDateTimePart = (value: number | string) =>
  String(value).padStart(2, "0");

export const formatDate = (dateString: string | null) => {
  if (!dateString) return null;

  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

export const parseStringTimeToDate = (timeString: string | null) => {
  if (!timeString) return null;

  // Match "hh:mm AM/PM" or "hh:mm"
  const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const meridian = match[3]?.toUpperCase();

  if (meridian === "PM" && hours < 12) hours += 12;
  if (meridian === "AM" && hours === 12) hours = 0;

  const date = new Date();
  date.setHours(hours, minutes, 0, 0); // seconds & ms = 0

  return date;
};

export const exportToExcel = (
  headers: string[],
  data: any[],
  pageName: string,
) => {
  const exportHeaders = headers.filter(
    (header, index, headerList) =>
      headerList.findIndex(
        (item) => item.trim().toLowerCase() === header.trim().toLowerCase(),
      ) === index,
  );
  const exportData = data.map((row) =>
    exportHeaders.reduce((acc, header) => {
      acc[header] = row?.[header] ?? "";
      return acc;
    }, {} as Record<string, any>),
  );

  // Create worksheet with controlled header order
  const worksheet = utils.json_to_sheet(exportData, {
    header: exportHeaders,
    skipHeader: false, // automatically creates header row
  });

  // Create workbook
  const workbook = utils.book_new();

  const safeSheetName = getSafeExcelSheetName(pageName);
  utils.book_append_sheet(workbook, worksheet, safeSheetName);

  // Download file
  writeFileXLSX(workbook, pageName);
};

export const downloadXMLTemplateFormat = (filename: string) => {
  const link = document.createElement("a");
  link.href = `/${filename}.xlsx`; // exact name
  link.download = `${filename}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type ExcelCellValue = string | number;

type ExcelDropdownConfig = {
  columnIndex: number;
  sheetName?: string;
  values?: ExcelCellValue[];
  allowCustomValue?: boolean;
  promptTitle?: string;
  prompt?: string;
  cellFormat?: "text";
};

const escapeXml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const columnName = (index: number) => {
  let name = "";
  let current = index;

  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }

  return name;
};

const worksheetXml = (
  rows: ExcelCellValue[][],
  dropdowns: ExcelDropdownConfig[] = [],
  totalColumns: number = rows[0]?.length ?? 1,
) => {
  const lastRow = Math.max(rows.length, 1);
  const validationLastRow = 1048576;
  const textColumnStyles = dropdowns
    .filter((dropdown) => dropdown.cellFormat === "text")
    .map((dropdown) => dropdown.columnIndex);
  const cols =
    textColumnStyles.length > 0
      ? `<cols>${textColumnStyles
          .map(
            (columnIndex) =>
              `<col min="${columnIndex}" max="${columnIndex}" width="18" customWidth="1" style="1"/>`,
          )
          .join("")}</cols>`
      : "";
  const sheetData = rows
    .map((row, rowIndex) => {
      const cells = row
        .map((cell, colIndex) => {
          const ref = `${columnName(colIndex + 1)}${rowIndex + 1}`;
          const style = textColumnStyles.includes(colIndex + 1) ? ' s="1"' : "";
          if (typeof cell === "number" && Number.isFinite(cell)) {
            return `<c r="${ref}"${style}><v>${cell}</v></c>`;
          }

          return `<c r="${ref}" t="inlineStr"${style}><is><t>${escapeXml(
            cell,
          )}</t></is></c>`;
        })
        .join("");

      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");

  const dataValidations =
    dropdowns.length > 0
      ? `<dataValidations count="${dropdowns.length}">${dropdowns
          .map((dropdown) => {
            const column = columnName(dropdown.columnIndex);
            const promptAttrs =
              dropdown.prompt || dropdown.promptTitle
                ? ` showInputMessage="1" promptTitle="${escapeXml(
                    dropdown.promptTitle ?? "",
                  )}" prompt="${escapeXml(dropdown.prompt ?? "")}"`
                : "";

            if (!dropdown.sheetName) {
              return `<dataValidation type="custom" allowBlank="1" showErrorMessage="0"${promptAttrs} sqref="${column}2:${column}${validationLastRow}"><formula1>TRUE</formula1></dataValidation>`;
            }

            const itemCount = Math.max(dropdown.values?.length ?? 0, 1);

            return `<dataValidation type="list" allowBlank="1" showErrorMessage="${
              dropdown.allowCustomValue ? "0" : "1"
            }"${promptAttrs} sqref="${column}2:${column}${validationLastRow}"><formula1>'${escapeXml(
              dropdown.sheetName,
            )}'!$A$1:$A$${itemCount}</formula1></dataValidation>`;
          })
          .join("")}</dataValidations>`
      : "";

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="A1:${columnName(totalColumns)}${lastRow}"/>
  <sheetViews><sheetView workbookViewId="0"/></sheetViews>
  <sheetFormatPr defaultRowHeight="15"/>
  ${cols}
  <sheetData>${sheetData}</sheetData>
  ${dataValidations}
  <pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>
</worksheet>`;
};

const worksheetXmlWithWrappedCells = (
  headers: string[],
  data: Record<string, ExcelCellValue>[],
  wrapColumnIndexes: number[] = [],
) => {
  const rows: ExcelCellValue[][] = [
    headers,
    ...data.map((row) => headers.map((header) => row[header] ?? "")),
  ];
  const lastRow = Math.max(rows.length, 1);
  const wrapColumns = new Set(wrapColumnIndexes);
  const cols = headers
    .map((_, index) => {
      const columnIndex = index + 1;
      const width = wrapColumns.has(columnIndex) ? 18 : 24;
      return `<col min="${columnIndex}" max="${columnIndex}" width="${width}" customWidth="1"/>`;
    })
    .join("");

  const sheetData = rows
    .map((row, rowIndex) => {
      const rowNumber = rowIndex + 1;
      const rowHasWrappedValue = row.some(
        (cell, index) =>
          wrapColumns.has(index + 1) && String(cell ?? "").includes("\n"),
      );
      const rowAttrs =
        rowIndex > 0 && rowHasWrappedValue ? ' ht="42" customHeight="1"' : "";
      const cells = row
        .map((cell, colIndex) => {
          const ref = `${columnName(colIndex + 1)}${rowNumber}`;
          const style = rowIndex === 0 ? ' s="1"' : wrapColumns.has(colIndex + 1) ? ' s="2"' : "";
          const value = String(cell ?? "");
          const preserveSpace = /\s/.test(value) ? ' xml:space="preserve"' : "";

          if (typeof cell === "number" && Number.isFinite(cell)) {
            return `<c r="${ref}"${style}><v>${cell}</v></c>`;
          }

          return `<c r="${ref}" t="inlineStr"${style}><is><t${preserveSpace}>${escapeXml(
            value,
          )}</t></is></c>`;
        })
        .join("");

      return `<row r="${rowNumber}"${rowAttrs}>${cells}</row>`;
    })
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="A1:${columnName(headers.length)}${lastRow}"/>
  <sheetViews><sheetView workbookViewId="0"/></sheetViews>
  <sheetFormatPr defaultRowHeight="15"/>
  <cols>${cols}</cols>
  <sheetData>${sheetData}</sheetData>
  <pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>
</worksheet>`;
};

const crcTable = (() => {
  const table: number[] = [];

  for (let i = 0; i < 256; i++) {
    let c = i;

    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }

    table[i] = c >>> 0;
  }

  return table;
})();

const crc32 = (data: Uint8Array) => {
  let crc = 0xffffffff;

  for (let i = 0; i < data.length; i++) {
    crc = crcTable[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
};

const writeUint16 = (bytes: number[], value: number) => {
  bytes.push(value & 0xff, (value >>> 8) & 0xff);
};

const writeUint32 = (bytes: number[], value: number) => {
  bytes.push(
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff,
  );
};

const createZipBlob = (files: { name: string; content: string }[]) => {
  const encoder = new TextEncoder();
  const fileRecords: {
    name: string;
    nameBytes: Uint8Array;
    data: Uint8Array;
    crc: number;
    offset: number;
  }[] = [];
  const output: number[] = [];

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const data = encoder.encode(file.content);
    const crc = crc32(data);
    const offset = output.length;

    writeUint32(output, 0x04034b50);
    writeUint16(output, 20);
    writeUint16(output, 0x0800);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint32(output, crc);
    writeUint32(output, data.length);
    writeUint32(output, data.length);
    writeUint16(output, nameBytes.length);
    writeUint16(output, 0);
    output.push(...Array.from(nameBytes), ...Array.from(data));

    fileRecords.push({ name: file.name, nameBytes, data, crc, offset });
  });

  const centralDirectoryOffset = output.length;

  fileRecords.forEach((file) => {
    writeUint32(output, 0x02014b50);
    writeUint16(output, 20);
    writeUint16(output, 20);
    writeUint16(output, 0x0800);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint32(output, file.crc);
    writeUint32(output, file.data.length);
    writeUint32(output, file.data.length);
    writeUint16(output, file.nameBytes.length);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint16(output, 0);
    writeUint32(output, 0);
    writeUint32(output, file.offset);
    output.push(...Array.from(file.nameBytes));
  });

  const centralDirectorySize = output.length - centralDirectoryOffset;

  writeUint32(output, 0x06054b50);
  writeUint16(output, 0);
  writeUint16(output, 0);
  writeUint16(output, fileRecords.length);
  writeUint16(output, fileRecords.length);
  writeUint32(output, centralDirectorySize);
  writeUint32(output, centralDirectoryOffset);
  writeUint16(output, 0);

  return new Blob([new Uint8Array(output)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadExcelTemplateWithDropdowns = (
  headers: string[],
  filename: string,
  dropdowns: ExcelDropdownConfig[],
  sheetName: string = "PLC Events",
) => {
  const lookupDropdowns = dropdowns.filter(
    (dropdown) => dropdown.sheetName && dropdown.values,
  );
  const workbookSheets = [
    { name: sheetName, hidden: false },
    ...lookupDropdowns.map((dropdown) => ({
      name: dropdown.sheetName ?? "",
      hidden: true,
    })),
  ];

  const files = [
    {
      name: "[Content_Types].xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  ${workbookSheets
    .map(
      (_, index) =>
        `<Override PartName="/xl/worksheets/sheet${
          index + 1
        }.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    )
    .join("")}
</Types>`,
    },
    {
      name: "_rels/.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      name: "xl/workbook.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    ${workbookSheets
      .map(
        (sheet, index) =>
          `<sheet name="${escapeXml(sheet.name)}" sheetId="${index + 1}"${
            sheet.hidden ? ' state="hidden"' : ""
          } r:id="rId${index + 1}"/>`,
      )
      .join("")}
  </sheets>
</workbook>`,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${workbookSheets
    .map(
      (_, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${
          index + 1
        }.xml"/>`,
    )
    .join("")}
  <Relationship Id="rId${workbookSheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    {
      name: "xl/styles.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>
  <fills count="1"><fill><patternFill patternType="none"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="2">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`,
    },
    {
      name: "xl/worksheets/sheet1.xml",
      content: worksheetXml([headers], dropdowns, headers.length),
    },
    ...lookupDropdowns.map((dropdown, index) => ({
      name: `xl/worksheets/sheet${index + 2}.xml`,
      content: worksheetXml(
        dropdown.values?.length
          ? dropdown.values.map((value) => [value])
          : [[""]],
        [],
        1,
      ),
    })),
  ];

  const blob = createZipBlob(files);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportToExcelWithWrappedCells = (
  headers: string[],
  data: Record<string, ExcelCellValue>[],
  pageName: string,
  wrapColumnIndexes: number[] = [],
) => {
  const sheetName = getSafeExcelSheetName(pageName);
  const files = [
    {
      name: "[Content_Types].xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`,
    },
    {
      name: "_rels/.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      name: "xl/workbook.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    {
      name: "xl/styles.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><name val="Calibri"/></font>
  </fonts>
  <fills count="2">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFBFDAEC"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="3">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="1" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment wrapText="1" vertical="top"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`,
    },
    {
      name: "xl/worksheets/sheet1.xml",
      content: worksheetXmlWithWrappedCells(headers, data, wrapColumnIndexes),
    },
  ];

  const blob = createZipBlob(files);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = pageName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

type DebouncedFunction<T extends (...args: any[]) => void> = {
  (...args: Parameters<T>): void;
  cancel: () => void;
};

export const searchDebounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): DebouncedFunction<T> => {
  let timeout: NodeJS.Timeout;

  const debounced = ((...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  }) as DebouncedFunction<T>;

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return debounced;
};

export function handleValues(details: any) {
  const hasValue = Object.values(details || {}).every((value) => {
    if (typeof value === "string") {
      return value.trim().length > 0; // non-empty string
    } else if (Array.isArray(value)) {
      return value.length > 0; // non-empty array
    } else if (typeof value === "number") {
      return true; // numbers count as having a value
    } else if (typeof value === "boolean") {
      return value; // true counts as a value
    }
    return false; // null, undefined, objects => no value
  });
  return hasValue;
}

export const handleExportApiCall = async (
  url: string,
  dispatcher: any,
  name: string,
) => {
  try {
    let res = await apiClient?.get<any>(url);
    return res?.data?.data;
  } catch (error: any) {
    dispatcher(
      setSnackbarDetails({
        type: "error",
        message:
          error?.response?.data?.data?.message ??
          `Error In Fetching the ${name}`,
      }),
    );
  }
};

export const handleExportTableData = async (data: any, columns: columns[]) => {
  try {
    let exportData = data?.map((item: any) => {
      const row: any = {};

      columns?.forEach((col: any) => {
        let value = item[col.accessor];

        // Handle arrays
        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        //  Handle objects (avoid [object Object])
        else if (typeof value === "object" && value !== null) {
          value = JSON.stringify(value);
        }

        // Handle null/undefined
        row[col.header] = value ?? "";
      });

      return row;
    });

    return exportData || [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

export function getAnnualYearData() {
  const startYear = 2020;
  const currentYear = new Date().getFullYear();
  const years: OptionType[] = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => {
      const year = currentYear - i;

      let data = {
        id: String(i + 1),
        displayName: String(year),
      };
      return data;
    },
  );
  return years;
}
